import { Navigate, Outlet, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  AppShell,
  BuildingIcon,
  CardIcon,
  ChatIcon,
  HelpIcon,
  LockIcon,
  PulseIcon,
  SearchIcon,
  SettingsIcon,
  TagIcon,
  SparkIcon,
  type ShellNavGroup,
  type ShellSignOut,
} from "@teamna/ui/components/index.ts";
import { isApiError } from "@teamna/web-client/api/client.ts";
import { setActingAs } from "../lib/act-as.ts";
import { signOutOfControlCenter, useStaffMe } from "../lib/cc-api.ts";
import { SplashScreen } from "./root.tsx";

/**
 * The Control Center frame for the read views: the shared desktop shell with the admin accent and
 * ten destinations, Businesses, Support (the ticket board), Leads (the Lead Finder), Early access
 * (the website forms and the sign-up allow list), Billing (the feed), Costs (Twilio and AI by business), AI Assistant (the review of the chats), Demo clubs,
 * Health and Settings (the Teamna-wide switches). Without a staff
 * session the page goes to sign-in.
 */
const NAV: ShellNavGroup[] = [
  {
    items: [
      { to: "/businesses", label: "Businesses", icon: BuildingIcon },
      { to: "/support", label: "Support", icon: HelpIcon },
      { to: "/leads", label: "Leads", icon: SearchIcon },
      { to: "/early-access", label: "Early access", icon: LockIcon },
      { to: "/billing", label: "Billing", icon: TagIcon },
      { to: "/costs", label: "Costs", icon: CardIcon },
      { to: "/assistant", label: "AI Assistant", icon: ChatIcon },
      { to: "/demos", label: "Demo clubs", icon: SparkIcon },
      { to: "/health", label: "Health", icon: PulseIcon },
      { to: "/settings", label: "Settings", icon: SettingsIcon },
    ],
  },
];

export function useControlCenterSignOut(): ShellSignOut {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMemo(
    () => ({
      subtitle: "To sign in again, you need your @teamna.app Google account.",
      onSignOut: () => {
        void signOutOfControlCenter().then(() => {
          setActingAs(null);
          queryClient.clear();
          void navigate({ to: "/sign-in" });
        });
      },
    }),
    [navigate, queryClient],
  );
}

/** The staff session, or a redirect to sign-in. Children render only for a signed-in staff member. */
export function RequireStaff({ children }: { children: (staff: { email: string; name: string | null }) => React.ReactNode }) {
  const me = useStaffMe();
  if (me.data) return <>{children(me.data)}</>;
  if (me.isLoading) return <SplashScreen label="Opening Control Center" />;
  if (isApiError(me.error) && me.error.status === 401) return <Navigate to="/sign-in" replace />;
  return <SplashScreen label="Control Center is not reachable" />;
}

export function StaffLayout() {
  const signOut = useControlCenterSignOut();
  return (
    <RequireStaff>
      {(staff) => (
        <AppShell
          brand="Control Center"
          groups={NAV}
          bottom={[]}
          user={{ name: staff.name ?? staff.email, detail: staff.email }}
          signOut={signOut}
          sidebarTone="dark"
        >
          <Outlet />
        </AppShell>
      )}
    </RequireStaff>
  );
}
